import BaseService from '../BaseService';
import { DEALCARD_BASE_URL } from '@/constants';
import type { GenericPage } from '@/types';
import { type CompanyViewModel } from '@/types/dealcard/company';
import {
  type DealAssetAgregation,
  type TopInvestor,
  type DealDetail,
  type Issue,
  type DealsByRole,
  type DownloadRequest,
  type NewDealRequest,
  type DealStatus,
  type DealViewModel,
} from '@/types/dealcard/deal';
import { type DefinitionViewModel } from '@/types/dealcard/definition';
import { type OrderViewModel } from '@/types/dealcard/order';
import { type TrancheViewModel } from '@/types/dealcard/tranche';

export interface RecentIssueParams {
  filter?: string;
  pageNumber?: number;
  pageSize?: number;
}

export class DealService extends BaseService {
  private baseURL = DEALCARD_BASE_URL;

  getDetailsById = (id: string): Promise<DealDetail> => {
    return this.get<DealDetail>({ baseUrl: this.baseURL, path: `Deals/${id}/Details` });
  };

  getDefinitions = (pageNumber = 1, pageSize = 25): Promise<Record<string, DefinitionViewModel>> => {
    return this.get<Record<string, DefinitionViewModel>>({
      baseUrl: this.baseURL,
      path: `Definitions?pageNumber=${pageNumber}&pageSize=${pageSize}`,
    });
  };

  getDefinitionByName = (name: string): Promise<DefinitionViewModel> => {
    return this.get<DefinitionViewModel>({
      baseUrl: this.baseURL,
      path: `Definitions/name/${name}`,
    });
  };

  getDefaultDefinitions = (): Promise<Array<DefinitionViewModel>> => {
    return this.get<Array<DefinitionViewModel>>({ baseUrl: this.baseURL, path: `Definitions/defaults` });
  };

  getDealsByAssetType = async (filter?: string | Array<string>): Promise<DealAssetAgregation[]> => {
    const path = 'Issues/dealsbyassetclass';
    if (filter && Array.isArray(filter)) {
      const reqs = filter.map(f => {
        let pathFiltered = `${path}?filter=${f}`;
        return this.get<DealAssetAgregation[]>({ baseUrl: this.baseURL, path: pathFiltered });
      });
      return (await Promise.all(reqs)).flat();
    }
    if (filter) {
      let pathFiltered = `${path}?filter=${filter}`;
      return this.get<DealAssetAgregation[]>({ baseUrl: this.baseURL, path: pathFiltered });
    }
    return this.get<DealAssetAgregation[]>({ baseUrl: this.baseURL, path });
  };

  getDealsByTopInvestor = async (filter?: string): Promise<TopInvestor[]> => {
    return this.get<TopInvestor[]>({ baseUrl: this.baseURL, path: `Issues/gettopinvestors`, params: { filter } });
  };
  getDealsByRole = async (filter?: string): Promise<DealsByRole[]> => {
    return this.get<DealsByRole[]>({ baseUrl: this.baseURL, path: `Issues/dealsbyrole`, params: { filter } });
  };
  updateDealStatus = async (id: string, status: DealStatus): Promise<void> => {
    return this.put({ baseUrl: this.baseURL, path: `Deals/${id}/Status/${status}` });
  };
  getRecentIssues = async (request?: RecentIssueParams): Promise<GenericPage<Issue>> => {
    return await this.get<GenericPage<Issue>>({
      baseUrl: this.baseURL,
      path: `Issues/dealsbyissues`,
      params: { ...request },
    });
  };

  getDealReport = async (request: DownloadRequest): Promise<Blob | void> => {
    const pathStr =
      request.reportType === 'RecentIssues' ? `deals/recentissuersdownload` : `deals/${request.id}/download`;
    const res = await this.get<Response>({
      baseUrl: this.baseURL,
      path: pathStr,
      params: { ...request },
      contentType: `application/octet-stream`,
    });
    return res.blob();
  };

  updateDeal = async (deal: DealViewModel): Promise<DealViewModel> => {
    return await this.put<DealViewModel, DealViewModel>({
      baseUrl: this.baseURL,
      path: `Deals/${deal.id}`,
      body: deal,
    });
  };

  deleteDeal = async (dealId: string): Promise<boolean> => {
    return await this.delete<boolean>({
      baseUrl: this.baseURL,
      path: `Deals/${dealId}`,
    });
  };

  updateDealTranches = async (deal: DealDetail): Promise<TrancheViewModel[]> => {
    return await this.put<TrancheViewModel[], TrancheViewModel[]>({
      baseUrl: this.baseURL,
      path: `Deals/${deal.details.id}/Tranches`,
      body: deal.tranches,
    });
  };

  createNewDeal = async (deal: NewDealRequest): Promise<DealDetail> => {
    return await this.post({
      baseUrl: this.baseURL,
      path: `Deals`,
      body: deal,
    });
  };

  getResultSetByPathAndFilter = async (path: string, filter?: string): Promise<any[]> => {
    if (filter) {
      path = `${path}?filter=${filter}`;
    }
    return this.get<[]>({ baseUrl: this.baseURL, path: path });
  };

  getOrderBookByDealId = (id: string): Promise<OrderViewModel[]> => {
    return this.get<OrderViewModel[]>({ baseUrl: this.baseURL, path: `Deals/${id}/Orders` });
  };

  updateOrderBook = async (deal: DealDetail): Promise<OrderViewModel[]> => {
    return this.put({
      baseUrl: this.baseURL,
      path: `Deals/${deal.details.id}/Orders`,
      body: deal.orders,
      contentType: 'application/json',
    });
  };

  getRatingCompanies(): Promise<CompanyViewModel[]> {
    return this.get<CompanyViewModel[]>({
      baseUrl: this.baseURL,
      path: `Companies/ratingcompanies`,
    });
  }

  getDealsReportByStatus = async (request: DownloadRequest): Promise<Blob | void> => {
    const response = await this.get<Response>({
      baseUrl: this.baseURL,
      path: 'Deals/Status',
      params: { ...request },
      contentType: 'application/octet-stream',
    });

    return response.blob();
  };
}

export default DealService;
